import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Progress, Button, Tag, Avatar, Empty, Divider, Modal, Alert } from 'antd';
import {
  PlayCircleOutlined,
  SafetyCertificateFilled,
  UserOutlined,
  ShopOutlined,
  CrownOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { learningApi } from '../../../api/learning.api.js';
import { subscriptionApi } from '../../../api/subscription.api.js';
import { planApi } from '../../../api/plan.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import { PaymentComingSoon } from '../../payment/components/PaymentComingSoon.jsx';
import { PAYMENT_ENABLED } from '../../../constants/payment.js';

export function LearnerDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [myCourses, setMyCourses] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesRes, subRes, plansRes] = await Promise.all([
        learningApi.getMyCourses(),
        subscriptionApi.getMySubscription().catch(() => ({ data: null })),
        planApi.getAllPlans().catch(() => ({ data: [] })),
      ]);
      setMyCourses(coursesRes.data || []);
      setSubscriptionData(subRes.data);
      setPlans(plansRes.data || []);
    } catch (err) {
      setError(err.message || 'Không thể tải tiến độ học tập');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return <LoadingSpinner minHeight="400px" tip="Đang tải tiến độ học tập và gói cước..." />;
  }

  if (error) {
    return <ErrorState title="Lỗi tải dữ liệu" subTitle={error} onRetry={fetchDashboardData} />;
  }

  const completedCourses = myCourses.filter((item) => item.progress?.isCompleted);
  const inProgressCourses = myCourses.filter((item) => !item.progress?.isCompleted);

  const activeSub = subscriptionData?.activeSubscription || user?.activeSubscription;
  const currentPlan = activeSub?.plan;
  const planToPay = selectedPlanForUpgrade || plans.find((p) => p.code === 'HOI_VIEN_TOAN_DIEN');

  const formatPrice = (price) => {
    if (price === 0) return '0đ (Miễn phí)';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  };

  const getPlanTagColor = (code) => {
    switch (code) {
      case 'HOI_VIEN_TOAN_DIEN':
        return 'purple';
      case 'TANG_TRUONG':
        return 'blue';
      case 'KHOI_NGHIEP':
        return 'orange';
      case 'DOI_TAC':
        return 'gold';
      default:
        return 'green';
    }
  };

  return (
    <div className="main-container">
      {/* Profile Overview Header Card */}
      <Card
        style={{
          borderRadius: 12,
          marginBottom: 24,
          border: '1px solid #e2e8f0',
          background: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        }}
        styles={{ body: { padding: '24px 28px' } }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Avatar
              src={user?.avatar}
              icon={!user?.avatar && <UserOutlined />}
              size={64}
              style={{ backgroundColor: '#0284c7' }}
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                  {user?.fullName}
                </h2>
                <Tag color="blue">{user?.position || 'Dược sĩ'}</Tag>
              </div>

              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                <ShopOutlined /> {user?.pharmacyName || 'Nhà thuốc đạt chuẩn GPP'} • {user?.email}
              </div>
            </div>
          </div>

          {/* Quick Stats Badges */}
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0284c7' }}>
                {myCourses.length}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Chuyên đề tham gia</div>
            </div>

            <Divider type="vertical" style={{ height: 38 }} />

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>
                {completedCourses.length}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Đã hoàn thành</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Subscription Card - Quản lý gói cước của học viên */}
      <Card
        style={{
          borderRadius: 12,
          marginBottom: 28,
          border: '1px solid #c4b5fd',
          background: 'linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)',
          boxShadow: '0 4px 14px rgba(124, 58, 237, 0.06)',
        }}
        styles={{ body: { padding: '24px 28px' } }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: '#7c3aed',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              <CrownOutlined />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#6b21a8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Gói Dịch Vụ Của Bạn:
                </span>
                <Tag color={getPlanTagColor(currentPlan?.code)} style={{ fontSize: 13, fontWeight: 700, padding: '2px 10px', borderRadius: 4, margin: 0 }}>
                  {currentPlan?.name || 'Gói Hội Viên Đào Tạo'}
                </Tag>
                <Tag color={activeSub?.status === 'ACTIVE' ? 'green' : 'orange'}>
                  {activeSub?.status === 'ACTIVE' ? 'Đang hoạt động' : activeSub?.status || 'Active'}
                </Tag>
              </div>

              <p style={{ fontSize: 13, color: '#475569', margin: '6px 0 10px', maxWidth: 650 }}>
                {currentPlan?.description || 'Quyền truy cập học tập các module chuyên môn và tải file biểu mẫu công cụ.'}
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#64748b' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CalendarOutlined style={{ color: '#7c3aed' }} />
                  Kích hoạt: {activeSub?.startsAt ? new Date(activeSub.startsAt).toLocaleDateString('vi-VN') : 'Mặc định'}
                </span>
                {activeSub?.expiresAt && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CalendarOutlined style={{ color: '#dc2626' }} />
                    Hết hạn: {new Date(activeSub.expiresAt).toLocaleDateString('vi-VN')}
                  </span>
                )}
                <span>
                  Giá trị: <strong>{formatPrice(currentPlan?.price || 0)}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Giai đoạn thử nghiệm miễn phí: ẩn nút mua / nâng cấp gói hội viên */}
          {PAYMENT_ENABLED ? (
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              size="large"
              onClick={() => setIsUpgradeModalOpen(true)}
              style={{
                background: '#7c3aed',
                borderColor: '#7c3aed',
                fontWeight: 700,
                borderRadius: 8,
                height: 42,
                padding: '0 22px',
              }}
            >
              Nâng Cấp / Đổi Gói Học
            </Button>
          ) : (
            <Tag color="green" style={{ fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 8, margin: 0 }}>
              Đang trong giai đoạn thử nghiệm miễn phí
            </Tag>
          )}
        </div>
      </Card>

      {/* In-Progress Courses */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
          📚 Chuyên Đề Đang Học ({inProgressCourses.length})
        </h3>

        {inProgressCourses.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '32px 0', borderRadius: 10 }}>
            <Empty description="Bạn chưa bắt đầu khóa học nào. Hãy khám phá ngay danh sách chuyên đề!" />
            <Button
              type="primary"
              onClick={() => navigate('/courses')}
              style={{ marginTop: 16, background: '#0284c7', borderColor: '#0284c7' }}
            >
              Xem danh sách khóa học
            </Button>
          </Card>
        ) : (
          <Row gutter={[20, 20]}>
            {inProgressCourses.map(({ enrollmentId, course, progress }) => {
              if (!course) return null;
              const percent = progress?.completionPercentage || 0;

              return (
                <Col xs={24} sm={12} lg={8} key={enrollmentId}>
                  <Card
                    hoverable
                    style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}
                    styles={{ body: { padding: 18, display: 'flex', flexDirection: 'column', flex: 1 } }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Tag color={course.category?.color || '#0284c7'}>{course.category?.name || 'Chuyên đề'}</Tag>
                      <span style={{ fontSize: 12, color: '#64748b' }}>{course.totalLessons} bài học</span>
                    </div>

                    <h4 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.4, marginBottom: 12, flex: 1 }}>
                      {course.title}
                    </h4>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#64748b' }}>Tiến độ học tập:</span>
                        <strong style={{ color: '#0284c7' }}>{percent}%</strong>
                      </div>
                      <Progress percent={percent} strokeColor="#0284c7" size="small" showInfo={false} />
                    </div>

                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      block
                      onClick={() => navigate(`/courses/${course.slug || course.id}`)}
                      style={{ background: '#0284c7', borderColor: '#0284c7', borderRadius: 6, fontWeight: 600 }}
                    >
                      Tiếp tục học
                    </Button>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      {/* Completed Courses & Certificates */}
      {completedCourses.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
            🏆 Chuyên Đề Đã Tốt Nghiệp ({completedCourses.length})
          </h3>

          <Row gutter={[20, 20]}>
            {completedCourses.map(({ enrollmentId, course, progress }) => {
              if (!course) return null;

              return (
                <Col xs={24} sm={12} lg={8} key={enrollmentId}>
                  <Card
                    style={{
                      borderRadius: 10,
                      border: '1px solid #bbf7d0',
                      background: '#f0fdf4',
                    }}
                    styles={{ body: { padding: 18 } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <SafetyCertificateFilled style={{ color: '#16a34a', fontSize: 18 }} />
                      <Tag color="green" style={{ margin: 0 }}>Đã hoàn thành 100%</Tag>
                    </div>

                    <h4 style={{ fontSize: 15, fontWeight: 700, color: '#14532d', marginBottom: 12 }}>
                      {course.title}
                    </h4>

                    {progress?.certificateCode && (
                      <div style={{ fontSize: 12, color: '#15803d', background: '#dcfce7', padding: '6px 10px', borderRadius: 6, marginBottom: 14 }}>
                        Mã chứng chỉ: <strong>{progress.certificateCode}</strong>
                      </div>
                    )}

                    <Button
                      block
                      onClick={() => navigate(`/courses/${course.slug || course.id}`)}
                      style={{ borderColor: '#86efac', color: '#15803d', borderRadius: 6 }}
                    >
                      Xem lại giáo trình
                    </Button>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      )}

      {/* Modal Nâng Cấp Gói Học: chọn gói rồi sang trang thanh toán VietQR */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, color: '#7c3aed' }}>
            <CrownOutlined /> Nâng Cấp Gói Học Viên & Đăng Ký Thu Phí
          </div>
        }
        open={isUpgradeModalOpen}
        onCancel={() => setIsUpgradeModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsUpgradeModalOpen(false)}>
            Đóng
          </Button>,
        ]}
        width={780}
      >
        {PAYMENT_ENABLED && (
          <Alert
            message="Chính sách nâng cấp gói dịch vụ"
            description="Thanh toán bằng quét mã VietQR. Gói được kích hoạt tự động ngay sau khi chuyển khoản thành công."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <div style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>
            1. Chọn Gói Cước Muốn Đăng Ký / Nâng Cấp:
          </h4>

          <Row gutter={[12, 12]}>
            {plans.map((p) => {
              const isSelected = selectedPlanForUpgrade?.id === p.id || (!selectedPlanForUpgrade && p.code === 'HOI_VIEN_TOAN_DIEN');
              return (
                <Col xs={24} sm={12} key={p.id || p.code}>
                  <div
                    onClick={() => setSelectedPlanForUpgrade(p)}
                    style={{
                      padding: '14px 16px',
                      borderRadius: 8,
                      border: isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                      background: isSelected ? '#faf5ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <Tag color={getPlanTagColor(p.code)} style={{ fontWeight: 700 }}>
                        {p.name}
                      </Tag>
                      <strong style={{ color: '#7c3aed', fontSize: 15 }}>{formatPrice(p.price)}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {p.durationMonths} tháng học • {p.description}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Thanh toán: QR chuyển khoản SePay ở trang /checkout; chưa mở thì báo đang triển khai */}
        {PAYMENT_ENABLED ? (
          <Button
            type="primary"
            block
            size="large"
            icon={<QrcodeOutlined />}
            disabled={!planToPay || planToPay.price === 0}
            onClick={() => navigate(`/checkout/${planToPay.id}`)}
            style={{ background: '#7c3aed', borderColor: '#7c3aed', fontWeight: 700 }}
          >
            2. Thanh toán gói {planToPay?.name || ''}
          </Button>
        ) : (
          <div style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <PaymentComingSoon planName={planToPay?.name} />
          </div>
        )}
      </Modal>
    </div>
  );
}
